
/**
 * Transform record to option
 *
 * @example
 *   ```ts
 *   const record = {
 *     key1: 'label1',
 *     key2: 'label2'
 *   };
 *   const options = transformRecordToOption(record);
 *   // [
 *   //   { value: 'key1', label: 'label1' },
 *   //   { value: 'key2', label: 'label2' }
 *   // ]
 *   ```;
 *
 * @param record
 */
export function transformRecordToOption<T extends Record<string, string>>(record: T) {
  return Object.entries(record).map(([value, label]) => ({
    value,
    label
  })) as CommonType.Option<keyof T>[];
}

/**
 * Translate options
 *
 * @param options
 */
export function translateOptions(options: CommonType.Option<string>[]) {
  return options.map(option => ({
    ...option,
  }));
}
interface DateDiff {
  years: number;
  months: number;
  days: number;
  title: string;
}
/**
 * @description 计算两个时间差
 */
export function useDiffDate(startDate: Date | string, endDate: Date | string): DateDiff {
  let yearsDiff = 0;
  let monthsDiff = 0;
  let daysDiff = 0;
  let title = ''

  startDate = new Date(startDate);
  endDate = new Date(endDate);
  // 计算年份和月份的差值
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth();
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth();

  // 计算月份差值
  yearsDiff = endYear - startYear;
  monthsDiff = endMonth - startMonth;

  // 处理月份差值为负数的情况
  if (monthsDiff < 0) {
    yearsDiff--;
    monthsDiff += 12;
  }

  // 使用 setFullYear 进行日期调整，以处理天数差值
  const tempDate = new Date(startDate);
  tempDate.setFullYear(endDate.getFullYear(), endDate.getMonth(), 0); // 设置为月末的前一天

  // 计算天数差值
  const maxDaysInStartMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
  const daysInStartMonth = maxDaysInStartMonth - startDate.getDate() + 1;
  const daysInEndMonth = endDate.getDate();
  daysDiff = daysInEndMonth - tempDate.getDate() + daysInStartMonth;

  // 调整月份和天数差值
  if (daysDiff >= maxDaysInStartMonth) {
    daysDiff -= maxDaysInStartMonth;
    monthsDiff++;
  }
  if (yearsDiff) {
    if (monthsDiff) {
      if (daysDiff) {
        title = `${yearsDiff}年${monthsDiff}月零${daysDiff}天`
      } else {
        title = `${yearsDiff}年${monthsDiff}月`
      }
    } else {
      title = `${yearsDiff}年`
    }
  } else {
    if (monthsDiff) {
      if (daysDiff) {
        title = `${monthsDiff}月零${daysDiff}天`
      } else {
        title = `${monthsDiff}月`
      }
    } else {
      title = `${daysDiff}天`
    }
  }

  return { years: yearsDiff, months: monthsDiff, days: daysDiff, title };
}

interface IdentityInfo {
  gender: number; // 性别，1:'男' or 2:'女'
  age: number; // 年龄
}

/**
 *
 * @param identityNumber 身份证号
 * @returns {IdentityInfo} {gender,age}
 */
export function parseIdentityNumber(identityNumber: string): IdentityInfo | null {
  if (identityNumber.length !== 18) {
    return null; // 简化处理，假设身份证号为18位
  }
  const year = parseInt(identityNumber.substr(6, 4), 10);
  const month = parseInt(identityNumber.substr(10, 2), 10);
  const day = parseInt(identityNumber.substr(12, 2), 10);

  const genderDigit = parseInt(identityNumber.substr(16, 1), 10);
  const gender = genderDigit % 2 === 0 ? 2 : 1;

  const today = new Date();
  const birthDate = new Date(year, month - 1, day); // 月份要减去1，因为月份从0开始计数

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return { gender, age };
}

/**
 * 简单实现防抖方法
 *
 * 防抖(debounce)函数在第一次触发给定的函数时，不立即执行函数，而是给出一个期限值(delay)，比如100ms。
 * 如果100ms内再次执行函数，就重新开始计时，直到计时结束后再真正执行函数。
 * 这样做的好处是如果短时间内大量触发同一事件，只会执行一次函数。
 *
 * @param fn 要防抖的函数
 * @param delay 防抖的毫秒数
 * @returns {Function}
 */
export function simpleDebounce(fn: any, delay = 100) {
  let timer: any | null = null;
  return function () {
    let args = arguments;
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      // @ts-ignore
      fn.apply(this, args);
    }, delay);
  };
}

/**
 * 截取字符串日期年月日部分
 *
 * @param dateStr 日期字符串
 */
export function getDateToYearMonthDay(dateStr: string) {
  const regex = /^\d{4}-\d{2}-\d{2}/;
  const match = dateStr.match(regex);
  return match ? match[0] : null;
}

/**
 * 获取当前日期时间
 */
export function getCurrentDateTime() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = String(now.getMonth() + 1).padStart(2, "0");;
  const currentDay = String(now.getDate()).padStart(2, "0");
  const currentHour = String(now.getHours()).padStart(2, "0");
  const currentMinute = String(now.getMinutes()).padStart(2, "0");
  const currentSecond = String(now.getSeconds()).padStart(2, "0");
  const currentDate = currentYear + '-' + currentMonth + '-' + currentDay + ' ' + currentHour + ':' + currentMinute + ':' + currentSecond
  return currentDate
}

/**
 * 判断链接是图片还是视频
 */
export function isImageOrVideo(url: string) {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif'];
  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'flv', 'avi'];
  const extension = url?.split('.')?.pop()?.toLowerCase();
  return imageExtensions.includes(extension as string) ? 'image' : videoExtensions.includes(extension as string) ? 'video' : 'unknown';
}
